import React, { useState } from 'react';
import { ArrowLeft, ArrowRight, Rocket, Scale, Layers, CheckCircle2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BackToTop from '../components/BackToTop';
import { toast } from 'sonner';

// Full-screen hero background (the mobile-phone-payment shot you provided)
const HERO_IMAGE = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/plq0dd5b_LANDING%20PAGE%20IMAGE.png';

const turnoverOptions = [
  'Under €/£1,000',
  '€/£1,000 to €/£10,000',
  '€/£10,000 to €/£50,000',
  '€/£50,000 to €/£100,000',
  'Over €/£100,000',
  'Not sure',
];

const setupOptions = [
  'Single location retail',
  'Multiple locations',
  'Mobile/delivery service',
  'Restaurant/hospitality',
  'Online business wanting in-person payments',
  'Not sure — need advice',
];

const benefits = [
  {
    icon: Rocket,
    title: 'Fast Setup and Deployment',
    body: 'Live in days, not weeks. Our streamlined setup process gets your POS system operational quickly, so you can start accepting payments and focus on growing your business.',
  },
  {
    icon: Scale,
    title: 'Highly Competitive Rates',
    body: "Transparent pricing that maximises your profits. We offer some of the market's most competitive rates with no hidden fees, helping you keep more of what you earn.",
  },
  {
    icon: Layers,
    title: 'Comprehensive Merchant Services',
    body: 'Everything you need in one place. From POS terminals to online payments and detailed reporting — our complete solution simplifies your payment processing while giving you full control and insights.',
  },
];

const Field = ({ label, required, children, testId }) => (
  <div className="mb-7" data-testid={testId}>
    <label className="block text-sm font-bold text-gray-800 mb-2">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
  </div>
);

const Radio = ({ name, value, checked, onChange, testId }) => (
  <label
    className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg border cursor-pointer transition-all duration-150 ${
      checked
        ? 'border-[#1F4E79] bg-[#1F4E79]/5 ring-1 ring-[#1F4E79]/20'
        : 'border-gray-200 bg-white hover:border-[#1F4E79]/40 hover:bg-gray-50'
    }`}
    data-testid={testId}
  >
    <input
      type="radio"
      name={name}
      value={value}
      checked={checked}
      onChange={() => onChange(value)}
      className="accent-[#1F4E79]"
    />
    <span className="text-sm text-gray-800">{value}</span>
  </label>
);

const GetStartedQuote = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    industry: '',
    turnover: '',
    setup: '',
    name: '',
    email: '',
    phone: '',
    company: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: typeof e === 'string' ? e : e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.industry || !form.turnover || !form.setup || !form.name || !form.email || !form.phone) {
      toast.error('Please complete all required fields.');
      return;
    }
    setSubmitting(true);
    // UI-only mockup — wire to backend when ready
    setTimeout(() => {
      toast.success("Thanks! We'll be in touch within 24 hours with your free quote.");
      setForm({ industry: '', turnover: '', setup: '', name: '', email: '', phone: '', company: '' });
      setSubmitting(false);
    }, 700);
  };

  return (
    <div className="min-h-screen flex flex-col relative" data-testid="get-started-page">
      {/* Fixed full-screen background image */}
      <div
        className="fixed inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        aria-hidden="true"
      />
      {/* Dark readability wash */}
      <div className="fixed inset-0 bg-gradient-to-br from-[#0a1929]/80 via-[#0a1929]/65 to-[#163a5f]/75" aria-hidden="true" />

      <div className="relative z-10 flex flex-col min-h-screen">
        <Header />
        <div className="h-20" />

        <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 pt-8 pb-16">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-white/85 hover:text-white font-medium mb-8 transition-colors duration-200"
            data-testid="get-started-back"
          >
            <ArrowLeft size={18} />
            Back
          </button>

          {/* Top header */}
          <div className="max-w-4xl mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#ef5b25]/20 border border-[#ef5b25]/40 text-[#ffcba6] text-[11px] font-bold uppercase tracking-[0.15em] mb-5">
              Free Quote — No Obligation
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.08] tracking-tight mb-4" data-testid="gs-title">
              Expert POS Solutions,<br />Tailored for You
            </h1>
            <p className="text-lg sm:text-xl text-white/90 leading-relaxed max-w-3xl font-medium">
              Whether you're starting up or scaling up, we'll match you with transparent pricing and expert guidance — all at no cost to you.
            </p>
          </div>

          {/* Form + Benefits layout */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Form — left/center */}
            <form
              onSubmit={handleSubmit}
              className="lg:col-span-8 bg-white rounded-xl shadow-2xl p-7 sm:p-9"
              data-testid="gs-form"
            >
              {/* 1. Industry */}
              <Field label="1. Your Business or Industry" required testId="gs-field-industry">
                <input
                  type="text"
                  value={form.industry}
                  onChange={update('industry')}
                  placeholder="e.g. retail, restaurant, mobile services, professional services"
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:border-[#1F4E79] focus:ring-2 focus:ring-[#1F4E79]/15 outline-none transition-all duration-150"
                  data-testid="gs-input-industry"
                />
              </Field>

              {/* 2. Turnover */}
              <Field label="2. Your Business Turnover" required testId="gs-field-turnover">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {turnoverOptions.map((opt, i) => (
                    <Radio
                      key={opt}
                      name="turnover"
                      value={opt}
                      checked={form.turnover === opt}
                      onChange={update('turnover')}
                      testId={`gs-turnover-${i}`}
                    />
                  ))}
                </div>
              </Field>

              {/* 3. Setup */}
              <Field label="3. Business Set Up Required" required testId="gs-field-setup">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {setupOptions.map((opt, i) => (
                    <Radio
                      key={opt}
                      name="setup"
                      value={opt}
                      checked={form.setup === opt}
                      onChange={update('setup')}
                      testId={`gs-setup-${i}`}
                    />
                  ))}
                </div>
              </Field>

              {/* 4. Contact */}
              <div data-testid="gs-field-contact">
                <label className="block text-sm font-bold text-gray-800 mb-3">4. Contact Details</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input
                    type="text"
                    value={form.name}
                    onChange={update('name')}
                    placeholder="Contact name *"
                    className="px-4 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:border-[#1F4E79] focus:ring-2 focus:ring-[#1F4E79]/15 outline-none transition-all duration-150"
                    data-testid="gs-input-name"
                  />
                  <input
                    type="email"
                    value={form.email}
                    onChange={update('email')}
                    placeholder="Email address *"
                    className="px-4 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:border-[#1F4E79] focus:ring-2 focus:ring-[#1F4E79]/15 outline-none transition-all duration-150"
                    data-testid="gs-input-email"
                  />
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={update('phone')}
                    placeholder="Phone number *"
                    className="px-4 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:border-[#1F4E79] focus:ring-2 focus:ring-[#1F4E79]/15 outline-none transition-all duration-150"
                    data-testid="gs-input-phone"
                  />
                  <input
                    type="text"
                    value={form.company}
                    onChange={update('company')}
                    placeholder="Company name (optional)"
                    className="px-4 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:border-[#1F4E79] focus:ring-2 focus:ring-[#1F4E79]/15 outline-none transition-all duration-150"
                    data-testid="gs-input-company"
                  />
                </div>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={submitting}
                className="group mt-7 inline-flex items-center gap-2.5 px-7 py-3.5 bg-[#1F4E79] text-white font-bold rounded-lg hover:bg-[#163a5f] hover:gap-3.5 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="gs-submit"
              >
                {submitting ? 'Sending…' : 'Get My Free Quote'}</button>

              <p className="text-xs text-gray-500 mt-4">
                <CheckCircle2 size={12} className="inline mr-1 text-emerald-600" />
                Your information is secure and used only to prepare your tailored quote.
              </p>
            </form>

            {/* Glass-morphism benefits — right, slightly pushed down to align top card with the first input field */}
            <aside className="lg:col-span-4 space-y-4 lg:pt-16" data-testid="gs-benefits">
              {benefits.map((b, i) => {
                const Icon = b.icon;
                return (
                  <div
                    key={i}
                    className="rounded-xl p-5 bg-white/10 backdrop-blur-md border border-white/20 shadow-xl hover:bg-white/15 transition-all duration-200"
                    data-testid={`gs-benefit-${i}`}
                  >
                    <div className="flex items-center gap-2.5 mb-2">
                      <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center border border-white/20">
                        <Icon size={18} className="text-white" strokeWidth={1.75} />
                      </div>
                      <h3 className="text-[15px] font-bold text-white leading-tight">{b.title}</h3>
                    </div>
                    <p className="text-[13px] text-white/85 leading-relaxed">{b.body}</p>
                  </div>
                );
              })}
            </aside>
          </div>
        </main>

        <Footer />
        <BackToTop />
      </div>
    </div>
  );
};

export default GetStartedQuote;
